/**
 * Тонкая жёлтая плашка-кнопка триала. Рендерится сверху Главной только когда
 * `subscription.status === 'trial'` (Phase 4 — Home.tsx читает store через
 * `getDaysRemaining()` и пробрасывает `showTrial` + `trialDays`). Кнопка ведёт
 * на покупку подписки (Phase 4 — stub, real /pricing будет в Block 11).
 *
 * Источник истины layout — прототип `screen-2-home.html` строка 411.
 */
export interface TrialBannerProps {
  trialDays: number;
  onClick: () => void;
}

/**
 * Русская плюрализация количества дней по правилам CLDR `few/many/one`:
 *
 *  - **one** («день»): n%10==1 И n%100 != 11 — 1, 21, 31, 101, ... 1001, ...
 *  - **few** («дня»): n%10 ∈ {2,3,4} И n%100 ∉ {12,13,14} — 2-4, 22-24, 32-34, ...
 *  - **many** («дней»): остальные — 0, 5-20, 25-30, 35-40, ...
 *
 * Phase 1 версия (`n < 5 → 'дня'`) была некорректна для 11-14 (давала «дней»
 * случайно правильно) и для 21-24, 31-34 и т.д. (давала «дней» вместо «дня/день»).
 * Phase 4 mock-timeline покрывает диапазон 0-30 дней (trial=12, grace=5,
 * paid=25, expired_readonly=28, archived=-1) — все случаи теперь корректны.
 */
function pluralizeDays(n: number): string {
  const absN = Math.abs(n);
  const last2 = absN % 100;
  if (last2 >= 11 && last2 <= 14) return 'дней';
  const last1 = absN % 10;
  if (last1 === 1) return 'день';
  if (last1 >= 2 && last1 <= 4) return 'дня';
  return 'дней';
}

export function TrialBanner({ trialDays, onClick }: TrialBannerProps) {
  if (trialDays <= 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Оплатить подписку со скидкой"
      className={
        'w-full px-4 py-3 text-left font-bold text-ink bg-cta border-b-2 border-cta-shadow ' +
        'flex items-center justify-between gap-2 ' +
        'focus-visible:ring-2 focus-visible:ring-ink focus-visible:outline-none ' +
        'transition-opacity active:opacity-90'
      }
      style={{ fontSize: 17 }}
    >
      <span>
        Триал · осталось {trialDays} {pluralizeDays(trialDays)} · скидка 30%
      </span>
      <span aria-hidden="true" style={{ fontSize: 18 }}>
        →
      </span>
    </button>
  );
}
